// =============================================================================
// MeuDriver — Instructor Financial Overview
// =============================================================================

import { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  DollarSign,
  TrendingUp,
  Percent,
  Download,
  ChevronLeft,
  ChevronRight,
  Info,
  ArrowUpCircle,
  CheckCircle2,
  Clock,
  Target,
  Pencil,
  Check,
  FileBarChart,
} from 'lucide-react'
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency, formatDate, getInitials } from '@/lib/utils'
import { PAYOUT_WINDOW_HOURS } from '@/lib/constants'
import { downloadCsv, formatDateBR, formatBRL } from '@/lib/csv'
import type { BookingRow, ProfileRow } from '@/types/database'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CompletedBooking = BookingRow & {
  student?: Pick<ProfileRow, 'full_name'>
}

interface MonthlyEarning {
  label: string        // e.g. "Jan/25"
  payout: number
  monthKey: string     // "YYYY-MM"
}

const PAGE_SIZE = 10

// ---------------------------------------------------------------------------
// CSV Export
// ---------------------------------------------------------------------------

function exportCsv(bookings: CompletedBooking[]) {
  const today = new Date().toISOString().slice(0, 10)
  downloadCsv(
    `financeiro-instrutor-${today}.csv`,
    bookings,
    [
      { key: 'scheduled_at',  label: 'Data da aula', map: (v) => formatDateBR(v) },
      { key: 'student',       label: 'Aluno', map: (_, b) => b.student?.full_name ?? '' },
      { key: 'duration_minutes', label: 'Duração (min)' },
      { key: 'booking_type',  label: 'Tipo' },
      { key: 'total_price',   label: 'Valor bruto', map: (v) => formatBRL(v) },
      { key: 'platform_fee',  label: 'Taxa plataforma', map: (v) => formatBRL(v) },
      { key: 'instructor_payout', label: 'Valor líquido', map: (v) => formatBRL(v) },
      {
        key: 'payout_processed_at',
        label: 'Status payout',
        map: (v) => (v ? `Pago em ${formatDateBR(v)}` : 'Pendente'),
      },
    ],
  )
  toast.success('Extrato exportado.')
}

// ---------------------------------------------------------------------------
// Simple SVG Bar Chart
// ---------------------------------------------------------------------------

function BarChart({ data }: { data: MonthlyEarning[] }) {
  const max = Math.max(...data.map((d) => d.payout), 1)
  const chartHeight = 160
  const barWidth = 36
  const gap = 12
  const chartWidth = data.length * (barWidth + gap) - gap

  return (
    <div className="overflow-x-auto">
      <svg
        width={chartWidth + 48}
        height={chartHeight + 48}
        className="block mx-auto"
        role="img"
        aria-label="Gráfico de ganhos mensais"
      >
        {/* Y-axis reference lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = chartHeight - ratio * chartHeight + 4
          const value = max * ratio
          return (
            <g key={ratio}>
              <line
                x1={40}
                y1={y}
                x2={chartWidth + 48}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth={1}
              />
              <text
                x={36}
                y={y + 4}
                textAnchor="end"
                fontSize={9}
                fill="#9ca3af"
              >
                {value >= 1000
                  ? `R$${(value / 1000).toFixed(0)}k`
                  : `R$${value.toFixed(0)}`}
              </text>
            </g>
          )
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const barHeight = (d.payout / max) * chartHeight
          const x = 44 + i * (barWidth + gap)
          const y = chartHeight - barHeight + 4
          return (
            <g key={d.monthKey}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                rx={4}
                fill={barHeight === 0 ? '#e5e7eb' : '#0d9488'}
                opacity={0.85}
              />
              {/* Value label on top */}
              {d.payout > 0 && (
                <text
                  x={x + barWidth / 2}
                  y={y - 4}
                  textAnchor="middle"
                  fontSize={8}
                  fill="#374151"
                  fontWeight="600"
                >
                  {d.payout >= 1000
                    ? `R$${(d.payout / 1000).toFixed(1)}k`
                    : `R$${d.payout.toFixed(0)}`}
                </text>
              )}
              {/* Month label below */}
              <text
                x={x + barWidth / 2}
                y={chartHeight + 20}
                textAnchor="middle"
                fontSize={9}
                fill="#6b7280"
              >
                {d.label}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Summary Card
// ---------------------------------------------------------------------------

function SummaryCard({
  title,
  value,
  icon: Icon,
  iconColor,
  loading,
  subtitle,
}: {
  title: string
  value: string
  icon: React.ElementType
  iconColor: string
  loading: boolean
  subtitle?: string
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${iconColor}`} />
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-28" />
        ) : (
          <>
            <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Payout Request Card
// ---------------------------------------------------------------------------

interface PayoutReq { id: string; amount: number; requested_at: string; pix_key?: string | null; bank_info?: string | null }

function PayoutRequestCard({ instructorId, availableAmount }: { instructorId: string | null; availableAmount: number }) {
  const [req, setReq] = useState<PayoutReq | null>(null)
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [amountStr, setAmountStr] = useState('')
  const [pixKey, setPixKey] = useState('')
  const [bankInfo, setBankInfo] = useState('')

  useEffect(() => {
    if (!instructorId) return
    supabase
      .from('payout_requests')
      .select('id, amount, requested_at, pix_key, bank_info')
      .eq('instructor_id', instructorId)
      .in('status', ['pending', 'processing'])
      .order('requested_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => setReq((data ?? null) as PayoutReq | null))
  }, [instructorId])

  function openDialog() {
    setAmountStr(String(availableAmount.toFixed(2)))
    setPixKey('')
    setBankInfo('')
    setDialogOpen(true)
  }

  async function handleRequest() {
    if (!instructorId) return
    const amount = parseFloat(amountStr.replace(',', '.'))
    if (isNaN(amount) || amount <= 0) {
      toast.error('Informe um valor válido.')
      return
    }
    if (amount > availableAmount + 0.01) {
      toast.error(`Valor maior que o disponível (${formatCurrency(availableAmount)}).`)
      return
    }
    if (!pixKey.trim() && !bankInfo.trim()) {
      toast.error('Informe ao menos uma chave Pix OU os dados bancários.')
      return
    }

    setLoading(true)
    const { data, error } = await (supabase
      .from('payout_requests') as unknown as {
      insert: (row: Record<string, unknown>) => {
        select: (cols: string) => { single: () => Promise<{ data: PayoutReq | null; error: Error | null }> }
      }
    })
      .insert({
        instructor_id: instructorId,
        amount,
        pix_key: pixKey.trim() || null,
        bank_info: bankInfo.trim() || null,
      })
      .select('id, amount, requested_at, pix_key, bank_info')
      .single()
    setLoading(false)
    if (error) {
      toast.error('Erro ao solicitar saque.')
    } else {
      setReq(data)
      setDialogOpen(false)
      toast.success('Solicitação de saque enviada! Processamento em até 2 dias úteis.')
    }
  }

  async function handleCancel() {
    if (!req) return
    setLoading(true)
    const { error } = await supabase
      .from('payout_requests')
      .delete()
      .eq('id', req.id)
    setLoading(false)
    if (error) {
      toast.error('Erro ao cancelar solicitação.')
    } else {
      setReq(null)
      toast.info('Solicitação cancelada.')
    }
  }

  const reqDate = req ? new Date(req.requested_at) : null

  return (
    <Card className="border-teal-200 bg-teal-50/40">
      <CardContent className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-teal-100 p-2 shrink-0">
              <ArrowUpCircle className="h-5 w-5 text-teal-700" />
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">Solicitar saque</p>
              {req ? (
                <div className="flex items-center gap-2 mt-1">
                  <Clock className="h-3.5 w-3.5 text-orange-500" />
                  <span className="text-xs text-orange-700 font-medium">Em análise</span>
                  <span className="text-xs text-muted-foreground">
                    · {formatCurrency(req.amount)} · {reqDate ? reqDate.toLocaleDateString('pt-BR') : ''}
                  </span>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Disponível:{' '}
                  <span className="font-semibold text-teal-700">
                    {formatCurrency(availableAmount)}
                  </span>
                </p>
              )}
            </div>
          </div>

          {req ? (
            <div className="flex items-center gap-2 shrink-0">
              <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-700">
                <Clock className="h-3 w-3" />
                Processando
              </span>
              <Button variant="ghost" size="sm" onClick={handleCancel} disabled={loading} className="text-xs text-muted-foreground h-7">
                Cancelar
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              disabled={availableAmount <= 0 || loading}
              onClick={openDialog}
              className="bg-teal-600 hover:bg-teal-700 shrink-0"
            >
              <ArrowUpCircle className="h-4 w-4 mr-1.5" />
              Solicitar saque
            </Button>
          )}
        </div>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Solicitar saque</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="payout-amount">Valor (R$)</Label>
              <Input
                id="payout-amount"
                inputMode="decimal"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                placeholder="0,00"
              />
              <p className="text-xs text-muted-foreground">
                Disponível: {formatCurrency(availableAmount)}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="payout-pix">Chave Pix</Label>
              <Input
                id="payout-pix"
                value={pixKey}
                onChange={(e) => setPixKey(e.target.value)}
                placeholder="CPF / e-mail / telefone / chave aleatória"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="payout-bank">Dados bancários (opcional, se não usar Pix)</Label>
              <Input
                id="payout-bank"
                value={bankInfo}
                onChange={(e) => setBankInfo(e.target.value)}
                placeholder="Banco, agência, conta e tipo"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleRequest} disabled={loading} className="bg-teal-600 hover:bg-teal-700">
              {loading ? 'Enviando...' : 'Confirmar solicitação'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Monthly Revenue Goal Card
// ---------------------------------------------------------------------------

function MonthlyGoalCard({
  instructorId,
  thisMonthPayout,
  loading,
}: {
  instructorId: string | null
  thisMonthPayout: number
  loading: boolean
}) {
  const key = instructorId ? `md-revenue-goal-${instructorId}` : null
  const [goal, setGoal] = useState<number>(() => {
    if (!key) return 0
    return Number(localStorage.getItem(key) ?? 0)
  })
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  function startEdit() {
    setDraft(goal > 0 ? String(goal) : '')
    setEditing(true)
  }

  function saveGoal() {
    const v = parseFloat(draft.replace(',', '.'))
    const next = isNaN(v) || v < 0 ? 0 : v
    setGoal(next)
    if (key) localStorage.setItem(key, String(next))
    setEditing(false)
    toast.success('Meta atualizada!')
  }

  const pct = goal > 0 ? Math.min(Math.round((thisMonthPayout / goal) * 100), 100) : 0
  const reached = goal > 0 && thisMonthPayout >= goal

  return (
    <Card className={reached ? 'border-emerald-300 bg-emerald-50/40' : ''}>
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <Target className={`h-4 w-4 ${reached ? 'text-emerald-600' : 'text-brand-600'}`} />
          Meta mensal de receita
        </CardTitle>
        {!editing && (
          <button
            onClick={startEdit}
            className="text-muted-foreground hover:text-brand-600 transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <Skeleton className="h-10 w-full" />
        ) : editing ? (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">R$</span>
            <Input
              ref={inputRef}
              type="number"
              min="0"
              step="50"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveGoal(); if (e.key === 'Escape') setEditing(false) }}
              className="h-9 max-w-[160px]"
              placeholder="Ex: 3000"
            />
            <Button size="sm" onClick={saveGoal} className="h-9 gap-1.5">
              <Check className="h-3.5 w-3.5" />
              Salvar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-9">
              Cancelar
            </Button>
          </div>
        ) : goal === 0 ? (
          <button
            onClick={startEdit}
            className="text-sm text-brand-600 hover:underline font-medium"
          >
            + Definir meta mensal
          </button>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {formatCurrency(thisMonthPayout)} de {formatCurrency(goal)}
              </span>
              <span className={`font-semibold ${reached ? 'text-emerald-600' : 'text-brand-700'}`}>
                {pct}%
              </span>
            </div>
            <div className="h-2.5 w-full rounded-full bg-gray-200 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${reached ? 'bg-emerald-500' : 'bg-brand-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            {reached && (
              <p className="text-xs font-medium text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Meta atingida este mês! 🎉
              </p>
            )}
            {!reached && goal > 0 && (
              <p className="text-xs text-muted-foreground">
                Faltam {formatCurrency(goal - thisMonthPayout)} para atingir a meta.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function InstructorFinancial() {
  const { user } = useAuth()

  const [instructorId, setInstructorId] = useState<string | null>(null)
  const [bookings, setBookings] = useState<CompletedBooking[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)

  // ── Resolve instructor ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!user?.id) return
    supabase
      .from('instructors')
      .select('id')
      .eq('profile_id', user.id)
      .single()
      .then(({ data }) => setInstructorId(data?.id ?? null))
  }, [user?.id])

  // ── Load completed bookings ───────────────────────────────────────────────

  useEffect(() => {
    if (!instructorId) return
    async function load() {
      setLoading(true)
      const { data } = await supabase
        .from('bookings')
        .select('*, student:profiles!bookings_student_id_fkey(full_name)')
        .eq('instructor_id', instructorId!)
        .eq('status', 'completed')
        .order('scheduled_at', { ascending: false })
      setBookings((data ?? []) as CompletedBooking[])
      setLoading(false)
    }
    load()
  }, [instructorId])

  // ── Derived values ────────────────────────────────────────────────────────

  const now = new Date()

  // All-time total
  const totalPayout = bookings.reduce((s, b) => s + (b.instructor_payout ?? 0), 0)
  const totalFee = bookings.reduce((s, b) => s + (b.platform_fee ?? 0), 0)

  // This month
  const monthStart = startOfMonth(now).toISOString()
  const monthEnd = endOfMonth(now).toISOString()
  const thisMonthPayout = bookings
    .filter((b) => b.scheduled_at >= monthStart && b.scheduled_at <= monthEnd)
    .reduce((s, b) => s + (b.instructor_payout ?? 0), 0)

  // Monthly chart data (last 6 months)
  const chartData: MonthlyEarning[] = Array.from({ length: 6 }, (_, i) => {
    const d = subMonths(now, 5 - i)
    const mStart = startOfMonth(d).toISOString()
    const mEnd = endOfMonth(d).toISOString()
    const payout = bookings
      .filter((b) => b.scheduled_at >= mStart && b.scheduled_at <= mEnd)
      .reduce((s, b) => s + (b.instructor_payout ?? 0), 0)
    return {
      label: format(d, "MMM/yy", { locale: ptBR }),
      payout,
      monthKey: format(d, 'yyyy-MM'),
    }
  })

  // Pagination
  const pagedBookings = bookings.slice(0, page * PAGE_SIZE)
  const hasMore = bookings.length > page * PAGE_SIZE

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Financeiro</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Acompanhe seus ganhos e repasses.
          </p>
        </div>
        <div className="flex gap-2 self-start sm:self-auto">
          <Button asChild variant="outline" size="sm" className="gap-1.5">
            <Link to="/instrutor/relatorio">
              <FileBarChart className="h-4 w-4" />
              Relatório PDF
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={loading || bookings.length === 0}
            onClick={() => exportCsv(bookings)}
          >
            <Download className="h-4 w-4" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard
          title="Total ganho"
          value={formatCurrency(totalPayout)}
          icon={TrendingUp}
          iconColor="text-green-600"
          loading={loading}
          subtitle="desde o início"
        />
        <SummaryCard
          title="Este mês"
          value={formatCurrency(thisMonthPayout)}
          icon={DollarSign}
          iconColor="text-teal-600"
          loading={loading}
          subtitle={format(now, "MMMM 'de' yyyy", { locale: ptBR })}
        />
        <SummaryCard
          title="Taxa retida"
          value={formatCurrency(totalFee)}
          icon={Percent}
          iconColor="text-orange-500"
          loading={loading}
          subtitle="15% plataforma (total)"
        />
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ganhos mensais — últimos 6 meses</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <BarChart data={chartData} />
          )}
        </CardContent>
      </Card>

      {/* Payout request */}
      <PayoutRequestCard instructorId={instructorId} availableAmount={thisMonthPayout} />

      {/* Monthly goal */}
      <MonthlyGoalCard
        instructorId={instructorId}
        thisMonthPayout={thisMonthPayout}
        loading={loading}
      />

      {/* Payout policy */}
      <div className="flex items-start gap-3 rounded-lg border border-brand-200 bg-brand-50 p-4">
        <Info className="h-5 w-5 text-brand-600 mt-0.5 shrink-0" />
        <div className="text-sm text-brand-800">
          <p className="font-medium">Política de repasse</p>
          <p className="mt-0.5 text-brand-700">
            O repasse é liberado automaticamente {PAYOUT_WINDOW_HOURS} horas após a
            conclusão da aula, descontada a taxa de 15% da plataforma. Em caso de
            dúvidas, entre em contato com o suporte.
          </p>
        </div>
      </div>

      {/* Bookings table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Histórico de aulas concluídas</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : bookings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <DollarSign className="h-10 w-10 text-gray-300 mb-3" />
              <p className="font-medium text-gray-500">Nenhuma aula concluída</p>
              <p className="text-sm text-muted-foreground mt-1">
                Suas aulas concluídas aparecerão aqui.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50 text-left">
                      <th className="px-4 py-3 font-medium text-muted-foreground">Data</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground">Aluno</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground">Duração</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground text-right">Valor total</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground text-right">Taxa (15%)</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground text-right">Repasse</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {pagedBookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-gray-50/50">
                        <td className="px-4 py-3 text-gray-700">
                          {formatDate(booking.scheduled_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700 text-xs font-semibold">
                              {getInitials((booking as CompletedBooking).student?.full_name ?? 'A')}
                            </div>
                            <span className="text-gray-900">
                              {(booking as CompletedBooking).student?.full_name ?? '—'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {booking.duration_minutes} min
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">
                          {booking.total_price != null
                            ? formatCurrency(booking.total_price)
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-right text-orange-600">
                          {booking.platform_fee != null
                            ? formatCurrency(booking.platform_fee)
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-green-700">
                          {booking.instructor_payout != null
                            ? formatCurrency(booking.instructor_payout)
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y">
                {pagedBookings.map((booking) => (
                  <div key={booking.id} className="px-4 py-3 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">
                        {(booking as CompletedBooking).student?.full_name ?? 'Aluno'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(booking.scheduled_at)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{booking.duration_minutes} min</span>
                      <div className="text-right space-y-0.5">
                        <p className="text-xs text-orange-500">
                          Taxa: {booking.platform_fee != null ? formatCurrency(booking.platform_fee) : '—'}
                        </p>
                        <p className="font-semibold text-green-700">
                          Repasse: {booking.instructor_payout != null ? formatCurrency(booking.instructor_payout) : '—'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {hasMore && (
                <div className="flex justify-center p-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Carregar mais
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
